import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

// How long a submit waits for a running puzzle before asking the visitor to
// retry. Usually a few seconds, but Friendly Captcha raises the difficulty for
// suspicious traffic (measured ~40 s after repeated solves from one IP) while
// the widget shows its own progress — so wait generously instead of
// interrupting a puzzle that is about to finish.
const SOLVE_TIMEOUT_MS = 60000;

// The SDK comes from the theme's own CDN. If it has not arrived by then, the
// visitor's network blocks it, and the form reports an error instead of hanging.
const SDK_TIMEOUT_MS = 15000;

// States in which the widget will not make progress without being started.
const IDLE_STATES = ['init', 'reset', 'unactivated', 'expired'];

/** @type {Promise<any> | undefined} */
let sdkPromise;

/**
 * Loads the vendored Friendly Captcha SDK (a classic script that defines
 * `window.frcaptcha`) once per page. Loading it contacts nobody and stores
 * nothing — that only starts when a widget is created.
 * @param {string} url
 * @returns {Promise<any>}
 */
function loadSdk(url) {
  if (window.frcaptcha) return Promise.resolve(window.frcaptcha);

  sdkPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    /** @param {string} message */
    const fail = (message) => {
      clearTimeout(timeout);
      script.remove();
      // Allow a later attempt, e.g. on the next submit.
      sdkPromise = undefined;
      reject(new Error(message));
    };
    const timeout = setTimeout(() => fail('Friendly Captcha SDK timed out'), SDK_TIMEOUT_MS);

    script.src = url;
    script.async = true;
    script.addEventListener('load', () => {
      clearTimeout(timeout);
      if (window.frcaptcha) resolve(window.frcaptcha);
      else fail('Friendly Captcha SDK loaded without defining window.frcaptcha');
    });
    script.addEventListener('error', () => fail('Friendly Captcha SDK failed to load'));
    document.head.append(script);
  });

  return sdkPromise;
}

/**
 * Raised by `solve()` when the captcha needs the visitor (interactive mode, or
 * a puzzle that did not finish in time). The form should ask them to complete it.
 */
export class CaptchaIncompleteError extends Error {
  constructor() {
    super('Friendly Captcha is not completed yet');
    this.name = 'CaptchaIncompleteError';
  }
}

/**
 * Friendly Captcha v2 widget for a theme form. It only produces a response —
 * the enclosing form must send that response to an endpoint that verifies it
 * with the Friendly Captcha API (the GCE app proxy, see docs/friendly-captcha.md).
 * A widget without server-side verification protects nothing.
 *
 * The widget is created when the visitor starts using the form, not on page
 * load: creating it connects to Friendly Captcha and keeps a session id in
 * sessionStorage, which should only happen for visitors who use the form.
 *
 * The widget follows the storefront language through the `lang` attribute on
 * `<html>`, which the SDK reads when no language is passed.
 *
 * @typedef {object} FriendlyCaptchaRefs
 * @property {HTMLElement} captchaMessage
 * @property {HTMLElement} captchaMessageText
 * @property {HTMLElement} captchaStatus
 *
 * @extends {Component<FriendlyCaptchaRefs>}
 */
export class FriendlyCaptcha extends Component {
  requiredRefs = ['captchaMessage', 'captchaMessageText', 'captchaStatus'];

  /** @type {any} */
  #widget = null;

  /** @type {HTMLElement | null} */
  #mount = null;

  /** @type {Promise<any> | null} */
  #creating = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  // Bumped on disconnect, so a creation that resumes after its element was
  // removed (and maybe re-inserted) does not mount a second widget.
  #generation = 0;

  /**
   * Reported by the widget: in `interactive` mode (set per application in the
   * Friendly Captcha dashboard) the puzzle only runs after the visitor clicks it.
   * @type {'interactive' | 'noninteractive' | undefined}
   */
  #mode;

  connectedCallback() {
    super.connectedCallback();
    requestIdleCallback(() => loadSdk(this.dataset.sdkUrl ?? '').catch(() => {}));
    this.#form = this.closest('form');
    this.#form?.addEventListener('focusin', this.#activate);
    // Re-connected while the visitor is typing (section re-render): no new focusin will come.
    if (this.#form?.contains(document.activeElement)) this.#activate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#form?.removeEventListener('focusin', this.#activate);
    this.#form = null;
    this.#generation++;
    this.#widget?.destroy();
    // destroy() only empties the mount; remove it so a re-connect starts clean.
    this.#mount?.remove();
    this.#widget = null;
    this.#mount = null;
    this.#creating = null;
  }

  /**
   * The Friendly Captcha region the widget fetches puzzles from.
   * @returns {'eu' | 'global'}
   */
  get apiEndpoint() {
    return this.dataset.apiEndpoint === 'eu' ? 'eu' : 'global';
  }

  /**
   * Resolves with the response to send to the verifying endpoint. Creates the
   * widget if the visitor has not touched the form yet, waits for a running
   * puzzle and starts one that has not started. In the error state the widget's
   * sentinel value is returned: the server passes it to Friendly Captcha, which
   * rejects it — unless the server cannot reach Friendly Captcha either, in
   * which case the server accepts the submission (fail-open).
   * @returns {Promise<string>}
   */
  async solve() {
    const widget = await this.#ensureWidget();

    const state = widget.getState();
    if (state === 'completed' || state === 'error') return widget.getResponse();
    if (this.#awaitsVisitor(state)) throw new CaptchaIncompleteError();
    if (IDLE_STATES.includes(state)) widget.start();

    this.#setBusy(true);
    try {
      return await new Promise((resolve, reject) => {
        const finish = () => {
          cleanUp();
          resolve(widget.getResponse());
        };
        const giveUp = () => {
          cleanUp();
          reject(new CaptchaIncompleteError());
        };
        /** @param {CustomEvent<{ state: string }>} event */
        const onStateChange = (event) => {
          if (this.#awaitsVisitor(event.detail.state)) giveUp();
        };
        const timeout = setTimeout(giveUp, SOLVE_TIMEOUT_MS);
        const cleanUp = () => {
          clearTimeout(timeout);
          widget.removeEventListener('frc:widget.complete', finish);
          widget.removeEventListener('frc:widget.error', finish);
          widget.removeEventListener('frc:widget.statechange', onStateChange);
        };

        widget.addEventListener('frc:widget.complete', finish);
        widget.addEventListener('frc:widget.error', finish);
        widget.addEventListener('frc:widget.statechange', onStateChange);
      });
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Starts a fresh puzzle. Required after every submission: a response can be
   * verified only once.
   */
  reset() {
    this.#widget?.reset();
  }

  /**
   * Shows a message below the widget and moves focus to it, so screen reader
   * users hear why the form did not submit.
   * @param {'incomplete' | 'failed'} reason
   */
  showMessage(reason) {
    const { captchaMessage, captchaMessageText } = this.refs;
    captchaMessageText.textContent =
      reason === 'incomplete' ? this.dataset.messageIncomplete ?? '' : this.dataset.messageFailed ?? '';
    captchaMessage.hidden = false;
    captchaMessage.focus();
  }

  hideMessage() {
    this.refs.captchaMessage.hidden = true;
  }

  #activate = () => {
    this.#ensureWidget().catch((error) => console.error('[friendly-captcha]', error));
  };

  /** @returns {Promise<any>} */
  #ensureWidget() {
    if (this.#widget) return Promise.resolve(this.#widget);

    this.#creating ??= this.#createWidget().catch((error) => {
      // Let the next interaction or submit try again.
      this.#creating = null;
      throw error;
    });
    return this.#creating;
  }

  async #createWidget() {
    const generation = this.#generation;
    const sdk = await loadSdk(this.dataset.sdkUrl ?? '');
    if (generation !== this.#generation || !this.isConnected) {
      throw new Error('Friendly Captcha element was removed while loading');
    }

    const mount = document.createElement('div');
    mount.className = 'friendly-captcha__widget';
    this.prepend(mount);

    const widget = sdk.createWidget({
      element: mount,
      sitekey: this.dataset.sitekey,
      apiEndpoint: this.apiEndpoint,
      // Created on the first interaction, so the puzzle can start right away.
      startMode: this.dataset.startMode === 'none' ? 'none' : 'auto',
    });
    // Registered before any solve() listener, so the mode is known when solve() reads it.
    widget.addEventListener('frc:widget.statechange', (/** @type {CustomEvent} */ event) => {
      if (event.detail.mode) this.#mode = event.detail.mode;
    });
    widget.addEventListener('frc:widget.complete', () => this.hideMessage());

    this.#mount = mount;
    this.#widget = widget;
    return widget;
  }

  /**
   * An interactive widget in the `activated` state waits for the visitor's
   * click — waiting on submit would only delay the message that asks for it.
   * @param {string} state
   */
  #awaitsVisitor(state) {
    return state === 'activated' && this.#mode === 'interactive';
  }

  /**
   * Announces a running verification through a polite live region; the widget
   * shows its own progress visually. (No aria-busy: it would hold back exactly
   * this announcement.)
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.refs.captchaStatus.textContent = busy ? this.dataset.messageVerifying ?? '' : '';
  }
}

if (!customElements.get('friendly-captcha')) {
  customElements.define('friendly-captcha', FriendlyCaptcha);
}
